import { useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useReducer } from "react";
import { useIntl } from "react-intl";
import { authApi } from "@repo/api";
import { useAuth } from "../../contexts/AuthContext";
import { SSO_AUTH_METHOD_KEY } from "../../constants/auth";

type LoginStep =
  | "username"
  | "password"
  | "forgotPassword"
  | "forgotPasswordConfirm"
  | "forgotPasswordSuccess"
  | "resetPassword"
  | "setupMfa"
  | "mfa"
  | "success";

interface LoginState {
  step: LoginStep;
  username: string;
  session: string;
  challengeName: string;
  qrCode: string;
  error: string;
}

type LoginAction =
  | { type: "SET_STEP"; step: LoginStep }
  | { type: "SET_USERNAME"; username: string }
  | { type: "SET_SESSION"; session: string }
  | { type: "SET_CHALLENGE"; challengeName: string }
  | { type: "SET_QR_CODE"; qrCode: string }
  | { type: "SET_ERROR"; error: string }
  | { type: "RESET" };

const initialState: LoginState = {
  step: "username",
  username: "",
  session: "",
  challengeName: "",
  qrCode: "",
  error: "",
};

function loginReducer(state: LoginState, action: LoginAction): LoginState {
  switch (action.type) {
    case "SET_STEP":
      return { ...state, step: action.step, error: "" };
    case "SET_USERNAME":
      return { ...state, username: action.username };
    case "SET_SESSION":
      return { ...state, session: action.session };
    case "SET_CHALLENGE":
      return { ...state, challengeName: action.challengeName };
    case "SET_QR_CODE":
      return { ...state, qrCode: action.qrCode };
    case "SET_ERROR":
      return { ...state, error: action.error };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

function appendHostToQrCode(qrCodeUrl: string): string {
  try {
    const url = new URL(qrCodeUrl);
    const host = window.location.hostname.split(".")[0];
    // otpauth://totp/<label>?issuer=<issuer>&secret=...
    // label is the pathname after the leading slash
    const label = decodeURIComponent(url.pathname.slice(1));
    const issuer = url.searchParams.get("issuer") ?? label.split(":")[0];
    const account = label.includes(":") ? label.split(":")[1] : label;
    const newIssuer = `${issuer} (${host})`;
    url.pathname = `/${encodeURIComponent(`${newIssuer}:${account.trim()}`)}`;
    url.searchParams.set("issuer", newIssuer);
    return url.toString();
  } catch {
    return qrCodeUrl;
  }
}

export function useLoginFlow() {
  const [state, dispatch] = useReducer(loginReducer, initialState);
  const navigate = useNavigate();
  const intl = useIntl();
  const auth = useAuth();

  const ssoInitiateMutation = useMutation({
    mutationFn: (email: string) => authApi.ssoInitiate(email),
    onSuccess: (response, email) => {
      // Persist the method so AuthContext can gate the Day-0 firstAccessRef
      // update on the correct auth response (Password → login/verify,
      // SSO → sso/callback). sessionStorage survives the IdP redirect.
      sessionStorage.setItem(SSO_AUTH_METHOD_KEY, response.method);
      if (response.method === "SSO" && response.redirectUrl) {
        sessionStorage.setItem("sso_pending_email", email);
        window.location.href = response.redirectUrl;
      } else {
        dispatch({ type: "SET_STEP", step: "password" });
      }
    },
    onError: () => {
      dispatch({
        type: "SET_ERROR",
        error: intl.formatMessage({
          id: "LI2xWG",
          defaultMessage: "Unable to determine login method. Please try again.",
          description: "Error when SSO initiation fails",
        }),
      });
    },
  });

  const loginMutation = useMutation({
    mutationFn: ({
      username,
      password,
    }: {
      username: string;
      password: string;
    }) => auth.login(username, password),
    onSuccess: async (response) => {
      if (response.success && response.accessToken) {
        navigate({ to: auth.getHomeRoute() });
      } else if (response.challengeName === "NEW_PASSWORD_REQUIRED") {
        dispatch({ type: "SET_SESSION", session: response.session || "" });
        dispatch({ type: "SET_STEP", step: "resetPassword" });
      } else if (response.challengeName === "MFA_SETUP") {
        await setupMfaMutation.mutateAsync({
          username: state.username,
          session: response.session || "",
        });
      } else if (response.challengeName === "SOFTWARE_TOKEN_MFA") {
        dispatch({ type: "SET_SESSION", session: response.session || "" });
        dispatch({
          type: "SET_CHALLENGE",
          challengeName: response.challengeName,
        });
        dispatch({ type: "SET_STEP", step: "mfa" });
      } else {
        dispatch({
          type: "SET_ERROR",
          error:
            response.message ||
            intl.formatMessage({
              id: "Xi0On3",
              defaultMessage: "Login failed",
              description: "Generic login failure error message",
            }),
        });
      }
    },
    onError: () => {
      dispatch({
        type: "SET_ERROR",
        error: intl.formatMessage({
          id: "bPfz6O",
          defaultMessage: "Invalid username or password",
          description: "Error when login credentials are incorrect",
        }),
      });
    },
  });

  const setupMfaMutation = useMutation({
    mutationFn: ({
      username,
      session,
    }: {
      username: string;
      session: string;
    }) => auth.setupMfa(username, session),
    onSuccess: (response) => {
      if (response.success && response.qrCodeUrl) {
        dispatch({ type: "SET_SESSION", session: response.session || "" });
        dispatch({
          type: "SET_QR_CODE",
          qrCode: appendHostToQrCode(response.qrCodeUrl),
        });
        dispatch({ type: "SET_STEP", step: "setupMfa" });
      } else {
        dispatch({
          type: "SET_ERROR",
          error:
            response.message ||
            intl.formatMessage({
              id: "hAgfu1",
              defaultMessage: "MFA setup failed",
              description: "Error when MFA setup fails",
            }),
        });
      }
    },
    onError: () => {
      dispatch({
        type: "SET_ERROR",
        error: intl.formatMessage({
          id: "hAgfu1",
          defaultMessage: "MFA setup failed",
          description: "Error when MFA setup fails",
        }),
      });
    },
  });

  const confirmMfaMutation = useMutation({
    mutationFn: ({
      username,
      mfaCode,
      session,
    }: {
      username: string;
      mfaCode: string;
      session: string;
    }) => auth.confirmMfa(username, mfaCode, session),
    onSuccess: (response) => {
      if (response.success && response.accessToken) {
        dispatch({ type: "SET_STEP", step: "success" });
      } else {
        dispatch({
          type: "SET_ERROR",
          error:
            response.message ||
            intl.formatMessage({
              id: "naaOR1",
              defaultMessage: "Setup failed",
              description: "Error when MFA confirmation setup fails",
            }),
        });
      }
    },
    onError: () => {
      dispatch({
        type: "SET_ERROR",
        error: intl.formatMessage({
          id: "rVah57",
          defaultMessage: "Incorrect passcode",
          description: "Error when MFA passcode is incorrect",
        }),
      });
    },
  });

  const verifyMfaMutation = useMutation({
    mutationFn: ({
      username,
      mfaCode,
      session,
      challengeName,
    }: {
      username: string;
      mfaCode: string;
      session: string;
      challengeName: string;
    }) => auth.verifyMfa(username, mfaCode, session, challengeName),
    onSuccess: (response) => {
      if (response.success && response.accessToken) {
        auth.completeAuth();
        navigate({ to: auth.getHomeRoute() });
      } else {
        dispatch({
          type: "SET_ERROR",
          error:
            response.message ||
            intl.formatMessage({
              id: "j9EqwC",
              defaultMessage: "Verification failed",
              description: "Error when MFA verification fails",
            }),
        });
      }
    },
    onError: () => {
      dispatch({
        type: "SET_ERROR",
        error: intl.formatMessage({
          id: "rVah57",
          defaultMessage: "Incorrect passcode",
          description: "Error when MFA passcode is incorrect",
        }),
      });
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({
      username,
      newPassword,
      session,
    }: {
      username: string;
      newPassword: string;
      session: string;
    }) => auth.resetPassword(username, newPassword, session),
    onSuccess: async (response) => {
      if (response.challengeName === "MFA_SETUP") {
        await setupMfaMutation.mutateAsync({
          username: state.username,
          session: response.session || "",
        });
      } else if (response.challengeName === "SOFTWARE_TOKEN_MFA") {
        dispatch({ type: "SET_SESSION", session: response.session || "" });
        dispatch({
          type: "SET_CHALLENGE",
          challengeName: response.challengeName,
        });
        dispatch({ type: "SET_STEP", step: "mfa" });
      } else {
        dispatch({
          type: "SET_ERROR",
          error:
            response.message ||
            intl.formatMessage({
              id: "U6J801",
              defaultMessage: "Password reset failed",
              description: "Error when password reset fails",
            }),
        });
      }
    },
    onError: () => {
      dispatch({
        type: "SET_ERROR",
        error: intl.formatMessage({
          id: "U6J801",
          defaultMessage: "Password reset failed",
          description: "Error when password reset fails",
        }),
      });
    },
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: ({ username }: { username: string }) =>
      auth.forgotPassword(username),
    onSuccess: () => {
      dispatch({ type: "SET_STEP", step: "forgotPasswordConfirm" });
    },
    onError: () => {
      dispatch({
        type: "SET_ERROR",
        error: intl.formatMessage({
          id: "YJM2nb",
          defaultMessage: "Failed to send reset email",
          description: "Error message when forgot password API call fails",
        }),
      });
    },
  });

  const confirmForgotPasswordMutation = useMutation({
    mutationFn: ({
      username,
      confirmationCode,
      newPassword,
    }: {
      username: string;
      confirmationCode: string;
      newPassword: string;
    }) => auth.confirmForgotPassword(username, confirmationCode, newPassword),
    onSuccess: () => {
      dispatch({ type: "SET_STEP", step: "forgotPasswordSuccess" });
    },
    onError: () => {
      dispatch({
        type: "SET_ERROR",
        error: intl.formatMessage({
          id: "SBb8uR",
          defaultMessage: "Password reset failed",
          description:
            "Error message when confirm forgot password API call fails",
        }),
      });
    },
  });

  const isLoading =
    loginMutation.isPending ||
    ssoInitiateMutation.isPending ||
    setupMfaMutation.isPending ||
    confirmMfaMutation.isPending ||
    verifyMfaMutation.isPending ||
    resetPasswordMutation.isPending ||
    forgotPasswordMutation.isPending ||
    confirmForgotPasswordMutation.isPending;

  return {
    state,
    dispatch,
    isLoading,
    ssoInitiateMutation,
    loginMutation,
    setupMfaMutation,
    confirmMfaMutation,
    verifyMfaMutation,
    resetPasswordMutation,
    forgotPasswordMutation,
    confirmForgotPasswordMutation,
    navigate,
  };
}
